import { LinkTo } from '@ember/routing';
import { Section, VStack } from 'tio-reimagined/components/layout';
import Heading from 'tio-reimagined/components/heading';
import Text from 'tio-reimagined/components/text';
import Chip from 'tio-reimagined/components/chip';
import PageHero from 'tio-reimagined/components/page-hero';
import DebtJourneyHero from 'tio-employee/components/reimagined/debt-journey-hero';
import style from 'ember-style-modifier/modifiers/style';
import { PhArrowLeft, PhPaintBrush, PhCode, PhPalette, PhSparkle } from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo
        @route="authenticated.reimagined.components"
        class="inline-flex items-center gap-2 tio-fg-brand dark:text-violet-400 hover:text-violet-800 dark:hover:text-violet-300 font-medium"
      >
        <PhArrowLeft @size={{18}} />
        Back to Components
      </LinkTo>
    </div>

    <PageHero
      @title="Gradients"
      @subtitle="Figma-driven branded gradient backgrounds for hero banners and feature sections."
      @icon={{PhPalette}}
      @theme="violet"
      @decoration="circles"
    >
      <:actions>
        <Chip @intent="primary">CSS Utility</Chip>
        <Chip @intent="success">Figma Synced</Chip>
      </:actions>
    </PageHero>

    <div class="space-y-8">

      {{! ========================================
            .bg-hero-gradient
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhPaintBrush @size={{20}} class="tio-fg-brand" />
            <span>bg-hero-gradient</span>
            <Chip @size="sm" @intent="primary">tio-ui</Chip>
            <Chip @size="sm" @intent="success">Figma</Chip>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            Multi-layered radial gradient with teal and purple glows on a dark charcoal base.
            Sourced from the Figma "Gradient" component. Apply via CSS class — no inline styles
            needed.
          </Text>

          {{! Live preview }}
          <div class="space-y-6">

            {{! Raw gradient swatch }}
            <div>
              <Text class="font-medium mb-2">Raw Swatch</Text>
              <div class="bg-hero-gradient rounded-2xl h-48 w-full"></div>
            </div>

            {{! With white text overlay }}
            <div>
              <Text class="font-medium mb-2">With Text Content</Text>
              <div class="bg-hero-gradient rounded-2xl p-8 text-white">
                <div class="flex items-center gap-3 mb-4">
                  <div class="p-3 bg-white/20 rounded-xl backdrop-blur-sm">
                    <PhSparkle @size={{28}} weight="duotone" />
                  </div>
                  <div>
                    <Heading @level={{2}} class="text-white text-2xl font-bold">
                      Hero Banner Title
                    </Heading>
                    <Text class="text-white/80">
                      Supporting text renders clearly on the gradient background.
                    </Text>
                  </div>
                </div>
                <div class="grid grid-cols-3 gap-4 pt-4 border-t border-white/20">
                  <div class="text-center">
                    <div class="text-2xl font-bold">$12,340</div>
                    <Text class="text-white/70 text-sm">Metric A</Text>
                  </div>
                  <div class="text-center border-l border-r border-white/20">
                    <div class="text-2xl font-bold">Aug 2029</div>
                    <Text class="text-white/70 text-sm">Metric B</Text>
                  </div>
                  <div class="text-center">
                    <div class="text-2xl font-bold">68%</div>
                    <Text class="text-white/70 text-sm">Metric C</Text>
                  </div>
                </div>
              </div>
            </div>

            {{! PageHero usage via @gradientClass }}
            <div>
              <Text class="font-medium mb-2">PageHero with @gradientClass</Text>
              <PageHero
                @title="Custom Gradient Hero"
                @subtitle="Using bg-hero-gradient via PageHero's @gradientClass prop for reusable themed banners."
                @icon={{PhPalette}}
                @gradientClass="bg-hero-gradient"
                @decoration="circles"
              >
                <:actions>
                  <Chip @intent="primary">Figma Synced</Chip>
                </:actions>
              </PageHero>
            </div>

            {{! DebtJourneyHero production usage }}
            <div>
              <Text class="font-medium mb-2">DebtJourneyHero (Production Component)</Text>
              <DebtJourneyHero
                @currentBalance={{45230}}
                @originalBalance={{68500}}
                @estimatedPayoffDate="2029-08-15"
                @yearProgressAmount={{2340}}
              />
            </div>
          </div>

          {{! Code example }}
          <div class="mt-6 bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <Text class="text-emerald-400 font-mono text-sm">
              {{! template-lint-disable no-bare-strings }}
              /* CSS class in tio-ui/styles.css */
            </Text>
            {{! template-lint-disable no-unnecessary-curly-strings }}
            <pre class="text-gray-300 font-mono text-sm mt-1"><code>.bg-hero-gradient
                {{"{"}}<br />
                background:<br />
                radial-gradient(600px 500px at 28% 150%, rgba(6, 89, 107, 0.5) 0%, transparent
                100%),<br />
                radial-gradient(800px 800px at -5% -60%, rgba(130, 51, 214, 0.5) 0%, transparent
                87%),<br />
                radial-gradient(500px 600px at 83% -60%, rgba(6, 89, 107, 0.5) 0%, transparent 55%),<br
                />
                radial-gradient(600px 600px at 90% 185%, rgba(130, 51, 214, 0.5) 0%, transparent
                64%),<br />
                #231e29;<br />{{"}"}}

              </code></pre>
          </div>

          {{! Usage patterns }}
          <div class="mt-6 space-y-3">
            <Text class="font-medium">Usage Patterns</Text>

            <div class="tio-bg-sunken dark:bg-gray-900 rounded-lg p-4">
              <div class="flex items-start gap-3">
                <PhCode @size={{20}} class="tio-fg-brand shrink-0 mt-0.5" />
                <div>
                  <Text class="font-medium">Direct CSS class</Text>
                  <pre class="tio-fg-secondary font-mono text-sm mt-1"><code>&lt;div
                      class="bg-hero-gradient rounded-2xl p-6 text-white"&gt;</code></pre>
                </div>
              </div>
            </div>

            <div class="tio-bg-sunken dark:bg-gray-900 rounded-lg p-4">
              <div class="flex items-start gap-3">
                <PhCode @size={{20}} class="tio-fg-brand shrink-0 mt-0.5" />
                <div>
                  <Text class="font-medium">Via PageHero @gradientClass</Text>
                  <pre class="tio-fg-secondary font-mono text-sm mt-1"><code>&lt;PageHero
                      @gradientClass="bg-hero-gradient" @title="..." /&gt;</code></pre>
                </div>
              </div>
            </div>
          </div>
        </:body>
      </Section>

      {{! ========================================
            Design Tokens Reference
            ======================================== }}
      <Section>
        <:header>
          <div class="flex items-center gap-2">
            <PhPalette @size={{20}} class="tio-fg-brand" />
            <span>Color Tokens</span>
          </div>
        </:header>
        <:body>
          <Text @variant="muted" class="mb-4">
            The gradient uses two key brand colors layered as radial glows. These values come
            directly from the Figma design system.
          </Text>

          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="#231e29"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text class="font-mono text-sm font-medium">#231e29</Text>
                <Text @variant="muted" class="text-xs">Base — Dark Charcoal</Text>
              </div>
            </div>

            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="rgb(6, 89, 107)"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text class="font-mono text-sm font-medium">rgb(6, 89, 107)</Text>
                <Text @variant="muted" class="text-xs">Accent — Teal</Text>
              </div>
            </div>

            <div class="rounded-lg overflow-hidden ring-1 ring-gray-200">
              <div class="h-16" {{style background="rgb(130, 51, 214)"}}></div>
              <div class="p-3 bg-white dark:bg-gray-900">
                <Text class="font-mono text-sm font-medium">rgb(130, 51, 214)</Text>
                <Text @variant="muted" class="text-xs">Accent — Purple</Text>
              </div>
            </div>
          </div>
        </:body>
      </Section>

    </div>
  </VStack>
</template>
